import apiHeader from "@/helpers/apiHeader";

function buildFormData(form) {
  const fd = new FormData();
  const appendIfPresent = (key, value) => {
    if (value !== undefined && value !== null) {
      fd.append(key, value);
    }
  };

  // Basic text fields
  appendIfPresent("mcr_code", form.mcr_code || "");
  // material_number hanya dikirim untuk type change-material (create-new tidak melempar material_number)
  if (form.material_number !== undefined) {
    appendIfPresent("material_number", form.material_number || "");
    appendIfPresent("mcr_type", form.mcr_type || "");
  }
  appendIfPresent("client", form.client || "");
  appendIfPresent("material_type", form.material_type || "");
  appendIfPresent("material_group", form.material_group || "");
  appendIfPresent("item_type", form.item_type || "");
  appendIfPresent("plant", form.plant || "");
  appendIfPresent("uom", form.uom || "");
  appendIfPresent("equipment", form.equipment || "");
  appendIfPresent("currency", form.currency || "");
  appendIfPresent("price_reference", form.price_reference || "");
  appendIfPresent("price_unit", form.price_unit ?? "");
  appendIfPresent("service_group", form.service_group || "");
  appendIfPresent("service_category", form.service_category || "");
  appendIfPresent("specification", form.specification || "");
  appendIfPresent(
    "descriptions",
    form.descriptions || form.specification || "",
  );
  appendIfPresent("valuation_class", form.valuation_class || "");
  appendIfPresent("standard_description", form.standard_description || "");
  appendIfPresent("estimated_annual_usage", form.estimated_annual_usage || "");
  // repairable_item: send 1 (true) or 0 (false)
  if (form.repairable_item !== undefined && form.repairable_item !== null && form.repairable_item !== '') {
    appendIfPresent("repairable_item", form.repairable_item === true || form.repairable_item === 1 ? 1 : 0);
  }

  // MRP Data (dikirim nested di bawah key emcr_mrp sesuai tab_fields config)
  // Hanya dikirim jika section MRP relevan untuk tipe MCR ini. Pengirim (UserForm.vue)
  // tidak menyertakan field MRP pada form jika tab MRP tidak visible, sehingga
  // blok emcr_mrp tidak dilempar sama sekali ketika field-nya undefined.
  const mrpFields = [
    "sparepart_category",
    "mrp_type",
    "lot_size",
    "reorder_point",
    "safety_stock",
    "abc_indicator",
    "maximum_stock_level",
    "rounding_value",
  ];
  const hasMrpData = mrpFields.some((key) => form[key] !== undefined);
  if (hasMrpData) {
    mrpFields.forEach((key) => {
      // Pakai nullish agar angka 0 tetap terkirim (bukan jadi string kosong)
      appendIfPresent(`emcr_mrp[${key}]`, form[key] ?? "");
    });
  }

  // Notes: check if form.notes is a string (from UserForm.vue) or array
  if (typeof form.notes === "string" && form.notes.trim() !== "") {
    fd.append("notes", form.notes);
  } else if (Array.isArray(form.notes)) {
    form.notes.forEach((note) => {
      if (note && note.notes) {
        fd.append(`notes`, note.notes);
      }
    });
  }

  // Manufacturers array
  (form.manufacturerParts || []).forEach((m, index) => {
    appendIfPresent(
      `emcr_manufacturers[${index}][manufacture]`,
      m.manufacture || "",
    );
    appendIfPresent(
      `emcr_manufacturers[${index}][manufacture_ref]`,
      m.manufacture_ref || "",
    );
    appendIfPresent(
      `emcr_manufacturers[${index}][manufacture_ref_description]`,
      m.manufacture_ref_description || m.notes || "",
    );
    appendIfPresent(`emcr_manufacturers[${index}][source_type]`, m.source_type || "");
    appendIfPresent(`emcr_manufacturers[${index}][manref_type]`, m.manref_type || "");
    appendIfPresent(`emcr_manufacturers[${index}][position]`, m.position != null ? m.position : (index + 1));
  });

  // Equipments array
  (form.equipmentItems || []).forEach((e, index) => {
    appendIfPresent(`emcr_equipments[${index}][plant]`, e.plant || "");
    appendIfPresent(`emcr_equipments[${index}][equipment]`, e.equipment || "");
    appendIfPresent(
      `emcr_equipments[${index}][qty_installed]`,
      e.qty_installed !== undefined && e.qty_installed !== null
        ? e.qty_installed
        : "",
    );
  });

  // Organization Level fields (ext_ prefix)
  appendIfPresent("ext_plant", form.ext_plant || "");
  appendIfPresent("ext_location", form.ext_location || "");
  appendIfPresent("ext_valuation_type", form.ext_valuation_type || "");
  appendIfPresent("ext_sales_org", form.ext_sales_org || "");
  appendIfPresent("ext_dist_channel", form.ext_dist_channel || "");
  appendIfPresent("ext_warehouse_number", form.ext_warehouse_number || "");
  appendIfPresent("ext_storage_type", form.ext_storage_type || "");

  // Copy From fields (ext_cf_ prefix)
  appendIfPresent("ext_cf_plant", form.ext_cf_plant || "");
  appendIfPresent("ext_cf_location", form.ext_cf_location || "");
  appendIfPresent("ext_cf_valuation_type", form.ext_cf_valuation_type || "");
  appendIfPresent("ext_cf_sales_org", form.ext_cf_sales_org || "");
  appendIfPresent("ext_cf_dist_channel", form.ext_cf_dist_channel || "");
  appendIfPresent("ext_cf_warehouse_number", form.ext_cf_warehouse_number || "");
  appendIfPresent("ext_cf_storage_type", form.ext_cf_storage_type || "");

  // Accounting fields
  appendIfPresent("price_control", form.price_control || "");
  appendIfPresent("standard_price", form.standard_price != null ? form.standard_price : "");

  // Valuation data (emcr_valuations)
  if (Array.isArray(form.emcr_valuations)) {
    form.emcr_valuations.forEach((v, index) => {
      appendIfPresent(`emcr_valuations[${index}][valuation_area]`, v.valuation_area || "");
      appendIfPresent(`emcr_valuations[${index}][valuation_class]`, v.valuation_class || "");
      appendIfPresent(`emcr_valuations[${index}][price_control]`, v.price_control || "");
      appendIfPresent(`emcr_valuations[${index}][std_price]`, v.std_price != null ? v.std_price : "");
    });
  }

  // Attachments:
  // - New files: File/Blob objects -> emcr_attachments[]
  // - Existing files by code: -> existing_attachments[]
  (form.attachments || []).forEach((att) => {
    if (!att) return;
    const isFileLike =
      (typeof File !== "undefined" && att instanceof File) ||
      (typeof Blob !== "undefined" && att instanceof Blob);
    if (isFileLike) {
      fd.append("emcr_attachments[]", att, att.name || "attachment");
    } else if (att.file_code) {
      fd.append("existing_attachments[]", att.file_code);
    }
  });

  return fd;
}

const userFormStore = {
  create(form) {
    const formData = buildFormData(form || {});
    return apiHeader.post("request-pool/user-form", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  createFromCataloguerForm(params) {
    return apiHeader.post("request-pool/user-form/create-from-cataloguer", params, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  updateFromCataloguerForm(id, params) {
    return apiHeader.post(`request-pool/user-form/update-from-cataloguer/${id}`, params, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  update(id, form) {
    const formData = buildFormData(form || {});
    return apiHeader.post(`request-pool/user-form/update/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  importEmcr(idEmcr, file, type) {
    const fd = new FormData();
    fd.append("file", file);
    return apiHeader.post(`request-pool/user-form/import/${idEmcr}`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      // Query: type = type EMCR (mentah) + id (id juga tetap dikirim via path)
      params: { type: type || undefined, id: idEmcr },
    });
  },
  delete(id) {
    return apiHeader.delete(`request-pool/user-form/${id}`);
  },
  checkDuplSpnInUserForm(mcrCode) {
    return apiHeader.get(`request-pool/user-form/check-duplicate-mcr/${mcrCode}`);
  }
};

export default userFormStore;
